import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class RetroSoundService {
  private audioCtx?: AudioContext;
  private isEnabled = false;
  private lastClickTime = 0;
  private readonly clickThrottleMs = 45;

  readonly isMuted = signal<boolean>(
    typeof localStorage !== 'undefined' && localStorage.getItem('siam_retro_sound_muted') === 'true'
  );

  /**
   * Initializes or resumes the Web Audio context safely
   */
  private getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') {
      return null;
    }
    if (!this.audioCtx) {
      const AudioContextClass =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioContextClass) {
        this.audioCtx = new AudioContextClass();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      void this.audioCtx.resume();
    }
    return this.audioCtx || null;
  }

  /**
   * Activate sound effects and click listener for the retro 90s theme
   */
  /**
   * Prime audio context on user gesture (e.g. login submit click)
   */
  initContext(): void {
    const ctx = this.getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume();
    }
  }

  /**
   * Activate sound effects and click listener for the retro 90s theme
   */
  enable(withStartupSound = false): void {
    if (this.isEnabled) {
      if (withStartupSound && !this.isMuted()) {
        this.playStartup();
      }
      return;
    }
    this.isEnabled = true;

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleClick, true);
    }

    if (withStartupSound && !this.isMuted()) {
      setTimeout(() => {
        if (this.isEnabled && !this.isMuted()) {
          this.playStartup();
        }
      }, 150);
    }
  }

  /**
   * Deactivate sounds and listeners when switching away from retro theme
   */
  disable(): void {
    this.isEnabled = false;
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleClick, true);
    }
    if (this.audioCtx) {
      try {
        void this.audioCtx.close();
      } catch {
        // audio context already closed or unavailable
      }
      this.audioCtx = undefined;
    }
  }

  /**
   * Toggle mute on/off with local persistence
   */
  toggleMute(): boolean {
    const nextState = !this.isMuted();
    this.isMuted.set(nextState);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('siam_retro_sound_muted', nextState ? 'true' : 'false');
    }
    if (!nextState) {
      this.playClick();
    }
    return nextState;
  }

  /**
   * Global click listener to intercept clicks on interactive elements
   */
  private readonly handleClick = (event: MouseEvent): void => {
    if (!this.isEnabled || this.isMuted()) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    // Detect clickable controls in the UI
    const isInteractive = target.closest(
      'button, a, .dx-button, .dx-tab, .dx-checkbox, .dx-list-item, ' +
        '.dx-treeview-item, .dx-menu-item, .nav-link, .nav-item, input[type="checkbox"], ' +
        'input[type="radio"], select, .btn, [role="button"], [role="tab"], [role="menuitem"]'
    );

    if (isInteractive) {
      this.playClick();
    }
  };

  /**
   * Classic Windows Explorer / Internet Explorer navigation click
   * Two rapid micro-impulses (down-click + release pop)
   */
  playClick(): void {
    if (this.isMuted()) {
      return;
    }
    const now = performance.now();
    if (now - this.lastClickTime < this.clickThrottleMs) {
      return;
    }
    this.lastClickTime = now;

    const ctx = this.getAudioContext();
    if (!ctx) {
      return;
    }

    const t = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.18, t);
    master.connect(ctx.destination);

    // Bandpass / lowpass filter to produce authentic mechanical mouse/relay snap
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2200, t);
    filter.connect(master);

    // Impulse 1 (down-click snap)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(1400, t);
    osc1.frequency.exponentialRampToValueAtTime(320, t + 0.009);
    gain1.gain.setValueAtTime(0.7, t);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.009);
    osc1.connect(gain1);
    gain1.connect(filter);
    osc1.start(t);
    osc1.stop(t + 0.012);

    // Impulse 2 (release micro-pop ~28ms later)
    const t2 = t + 0.028;
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(950, t2);
    osc2.frequency.exponentialRampToValueAtTime(260, t2 + 0.011);
    gain2.gain.setValueAtTime(0.45, t2);
    gain2.gain.exponentialRampToValueAtTime(0.001, t2 + 0.011);
    osc2.connect(gain2);
    gain2.connect(filter);
    osc2.start(t2);
    osc2.stop(t2 + 0.015);
  }

  /**
   * Iconic ethereal 90s startup chord (reminiscent of Brian Eno Windows 95)
   * Warm ambient chord across rich musical intervals fading out gently
   */
  playStartup(): void {
    if (this.isMuted()) {
      return;
    }
    const ctx = this.getAudioContext();
    if (!ctx) {
      return;
    }

    const t = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.14, t);
    master.connect(ctx.destination);

    // Ethereal chord (D-Major 9th: D3, A3, F#4, A4, E5, F#5)
    const notes = [
      { freq: 146.83, type: 'sine' as OscillatorType, gain: 0.35, attack: 0.15, decay: 2.8 },
      { freq: 220.0, type: 'triangle' as OscillatorType, gain: 0.25, attack: 0.25, decay: 2.6 },
      { freq: 369.99, type: 'sine' as OscillatorType, gain: 0.22, attack: 0.35, decay: 2.4 },
      { freq: 440.0, type: 'sine' as OscillatorType, gain: 0.2, attack: 0.45, decay: 2.2 },
      { freq: 659.25, type: 'triangle' as OscillatorType, gain: 0.18, attack: 0.6, decay: 2.0 },
      { freq: 739.99, type: 'sine' as OscillatorType, gain: 0.15, attack: 0.75, decay: 1.8 }
    ];

    notes.forEach(note => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = note.type;
      osc.frequency.setValueAtTime(note.freq, t);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(note.gain, t + note.attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + note.attack + note.decay);

      osc.connect(gain);
      gain.connect(master);
      osc.start(t);
      osc.stop(t + note.attack + note.decay + 0.1);
    });
  }

  /**
   * Classic Windows 95 Ding (crystal bell)
   */
  playDing(): void {
    if (this.isMuted()) {
      return;
    }
    const ctx = this.getAudioContext();
    if (!ctx) {
      return;
    }

    const t = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.2, t);
    master.connect(ctx.destination);

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t);

    gain.gain.setValueAtTime(0.8, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  /**
   * Classic Windows 95 Chord (system warning / alert)
   * Warm, resonant synthesizer chord (C-Major: C3, G3, C4, E4, G4)
   */
  playChord(): void {
    if (this.isMuted()) {
      return;
    }
    const ctx = this.getAudioContext();
    if (!ctx || ctx.state === 'suspended') {
      return;
    }

    const t = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.22, t);
    master.connect(ctx.destination);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2800, t);
    filter.frequency.exponentialRampToValueAtTime(600, t + 0.7);
    filter.connect(master);

    const notes = [
      { freq: 130.81, type: 'sawtooth' as OscillatorType, gain: 0.25 },
      { freq: 196.0, type: 'triangle' as OscillatorType, gain: 0.3 },
      { freq: 261.63, type: 'sawtooth' as OscillatorType, gain: 0.35 },
      { freq: 329.63, type: 'triangle' as OscillatorType, gain: 0.3 },
      { freq: 392.0, type: 'sawtooth' as OscillatorType, gain: 0.25 }
    ];

    notes.forEach(note => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = note.type;
      osc.frequency.setValueAtTime(note.freq, t);

      gain.gain.setValueAtTime(0.001, t);
      gain.gain.linearRampToValueAtTime(note.gain, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.85);

      osc.connect(gain);
      gain.connect(filter);
      osc.start(t);
      osc.stop(t + 0.9);
    });
  }
}
